import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { AlertCircle } from "lucide-react";
import type { FormEventHandler } from "react";
import Dropzone from "shadcn-dropzone";

import InputError from "@/components/input-error";
import PurchaseRequestPickerWithPreview from "@/components/purchase-request-picker-with-preview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FilePreview } from "@/components/ui/file-preview";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { ACCEPTED_FILE_TYPES } from "@/constants/file";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import AppLayout from "@/layouts/app-layout";
import type { BreadcrumbItem, SharedData } from "@/types";
import { getTodayDateString } from "@/utils/date";

export default function UploadNew() {
  const routes = useOrganizationRoutes();
  const { auth: _auth } = usePage<SharedData>().props;
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Remitos",
      href: routes.deliveryNotes.index(),
    },
    {
      title: "Cargar Remito",
      href: routes.deliveryNotes.uploadNew(),
    },
  ];
  const { data, setData, post, processing, progress, errors, transform } =
    useForm({
      receipt_date: getTodayDateString(), // Fecha actual (local)
      observations: "",
      remit_files: [] as File[],
      evidence_files: [] as File[],
      purchase_request_id: null as number | null,
    });

  // El procesamiento se realiza en background; la UI de progreso está en show

  const submit: FormEventHandler = (e) => {
    e.preventDefault();

    if (data.remit_files.length === 0) {
      return;
    }

    transform((formState) => {
      const formData = new FormData();
      formData.append("receipt_date", formState.receipt_date);
      formData.append("observations", formState.observations);

      if (formState.purchase_request_id !== null) {
        formData.append(
          "purchase_request_id",
          String(formState.purchase_request_id),
        );
      }

      formState.remit_files.forEach((file) => {
        formData.append("remit_files[]", file);
      });

      formState.evidence_files.forEach((file) => {
        formData.append("evidence_files[]", file);
      });

      return formData;
    });

    post(routes.deliveryNotes.index());
  };

  const handleRemitFilesChange = (acceptedFiles: File[]) => {
    // Agregar archivos en vez de reemplazar
    setData("remit_files", [...data.remit_files, ...acceptedFiles]);
  };

  const handleEvidenceFilesChange = (acceptedFiles: File[]) => {
    // Agregar archivos en vez de reemplazar
    setData("evidence_files", [...data.evidence_files, ...acceptedFiles]);
  };

  const removeRemitFile = (index: number) => {
    setData(
      "remit_files",
      data.remit_files.filter((_, i) => i !== index),
    );
  };

  const removeEvidenceFile = (index: number) => {
    setData(
      "evidence_files",
      data.evidence_files.filter((_, i) => i !== index),
    );
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Cargar Remito" />
      <div className="flex h-full flex-1 flex-col p-4 md:p-6">
        <div className="w-full max-w-4xl mx-auto">
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Carga rápida de remito</AlertTitle>
            <AlertDescription>
              Sube todas las páginas del remito y la IA extraerá automáticamente
              los productos y cantidades. Luego podrás vincular el remito con
              una solicitud de compra.
            </AlertDescription>
          </Alert>

          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle>Nuevo Remito</CardTitle>
              <CardDescription>
                Completa los datos básicos y adjunta los archivos del remito
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <form className="flex flex-col gap-4" onSubmit={submit}>
                {/* Fecha de recepción */}
                <div className="grid gap-2">
                  <Label htmlFor="receipt_date">Fecha de Recepción *</Label>
                  <input
                    type="date"
                    id="receipt_date"
                    max={getTodayDateString()}
                    value={data.receipt_date}
                    onChange={(e) => setData("receipt_date", e.target.value)}
                    disabled={processing}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <InputError message={errors.receipt_date} />
                </div>

                {/* Solicitud de Compra (opcional) */}
                <div className="grid gap-2">
                  <Label
                    htmlFor="purchase_request_id"
                    className="text-gray-500"
                  >
                    Opcionalmente puede vincular este remito con una solicitud
                    de compra
                  </Label>
                  <PurchaseRequestPickerWithPreview
                    value={data.purchase_request_id}
                    onChange={(value) => setData("purchase_request_id", value)}
                    disabled={processing}
                    placeholder="Buscar por número o sector..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Compra directa: se realiza desde una factura. Primero{" "}
                    <Link
                      href={routes.invoices.uploadNew()}
                      className="underline hover:text-foreground"
                    >
                      subí la factura
                    </Link>{" "}
                    para continuar.
                  </p>
                  <InputError message={errors.purchase_request_id} />
                </div>

                {/* Observaciones */}
                <div className="grid gap-2">
                  <Label htmlFor="observations">Observaciones</Label>
                  <TextareaWithCounter
                    id="observations"
                    value={data.observations}
                    onChange={(e) => setData("observations", e.target.value)}
                    disabled={processing}
                    rows={3}
                    placeholder="Agrega notas u observaciones sobre este remito..."
                    maxLength={500}
                    showCharacterCount={true}
                  />
                  <InputError message={errors.observations} />
                </div>

                {/* Archivos de Remito */}
                <div className="grid gap-2">
                  <Label>Remito (todas las páginas) *</Label>
                  <Dropzone
                    onDrop={handleRemitFilesChange}
                    accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                    maxFiles={10}
                    disabled={processing}
                    containerClassName="w-full"
                    dropZoneClassName="w-full h-32 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    {(dz) => (
                      <div className="text-center p-4">
                        <p className="text-sm text-muted-foreground">
                          {dz.isDragActive
                            ? "Suelta las páginas aquí"
                            : "Arrastra todas las páginas del remito o haz clic"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, JPG, PNG · Si el remito tiene varias páginas,
                          subí todas (hasta 10 archivos)
                        </p>
                      </div>
                    )}
                  </Dropzone>

                  {data.remit_files.length > 0 && (
                    <div className="grid gap-2 mt-2">
                      {data.remit_files.map((file, index) => (
                        <FilePreview
                          key={index}
                          file={file}
                          onRemove={() => removeRemitFile(index)}
                          disabled={processing}
                        />
                      ))}
                    </div>
                  )}
                  <InputError message={errors.remit_files} />
                </div>

                {/* Archivos de Evidencia (opcional) */}
                <div className="grid gap-2">
                  <Label>Evidencias (opcional)</Label>
                  <Dropzone
                    onDrop={handleEvidenceFilesChange}
                    accept={ACCEPTED_FILE_TYPES.PDF_AND_IMAGES}
                    maxFiles={10}
                    disabled={processing}
                    containerClassName="w-full"
                    dropZoneClassName="w-full h-24 rounded-lg border-2 border-dashed border-border hover:border-primary/50 transition-colors flex items-center justify-center cursor-pointer"
                  >
                    {(dz) => (
                      <div className="text-center p-4">
                        <p className="text-sm text-muted-foreground">
                          {dz.isDragActive
                            ? "Suelta las evidencias aquí"
                            : "Fotos de evidencia del remito (ticket de balance, recibos, etc.)"}
                        </p>
                      </div>
                    )}
                  </Dropzone>

                  {data.evidence_files.length > 0 && (
                    <div className="grid gap-2 mt-2">
                      {data.evidence_files.map((file, index) => (
                        <FilePreview
                          key={index}
                          file={file}
                          onRemove={() => removeEvidenceFile(index)}
                          disabled={processing}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Botones */}
                <div className="flex justify-end gap-2 pt-4">
                  {progress && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground pr-2">
                      <Spinner className="size-4" />
                      <span>Subiendo archivos…</span>
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.history.back()}
                    disabled={processing}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      !data.receipt_date ||
                      data.remit_files.length === 0 ||
                      processing
                    }
                  >
                    {processing ? (
                      <span className="flex items-center gap-2">
                        <Spinner className="size-4" />
                        Procesando…
                      </span>
                    ) : (
                      "Guardar y Procesar"
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* El procesamiento sucede en background y se refleja en show */}
    </AppLayout>
  );
}
